/* global ACCEPTED_ATTACHMENT_FILE_TYPES */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { format } from 'date-fns';
import { Field, FieldArray, Formik, useFormikContext } from 'formik';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import * as Yup from 'yup';

import ActivityEarliestStartLine from './ActivityEarliestStartLine';
import ActivityDependenciesTab from './dependencies/ActivityDependenciesTab';
import { shiftDates } from './dependencies/earliestStart';
import { lagSchema } from './dependencies/linkedActivity';
import useEarliestStart from './dependencies/useEarliestStart';
import ProjectActivityFormFooter from './ProjectActivityFormFooter';

import { defaultDatePickerFormat, defaultDatePickerValueFormat } from 'Helpers/dateUtils';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import DateRangePickerField from 'CommonComponents/DateRangePickerField';
import FormSectionHeading from 'CommonComponents/FormSectionHeading';
import Tabs, { RenderStrategyOnFirstVisit } from 'CommonComponents/Tabs';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextButton from 'CommonComponents/TextButton';
import TextFormField from 'CommonComponents/TextFormField';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import ProjectPeriodContext from 'Context/project_plan/ProjectPeriodContext';
import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

import { useUsers } from 'Hooks/useUsers';

import TrashIcon from 'Assets/icons/trash.svg';
import XMarkIcon from 'Assets/icons/xmark.svg';

const DEFAULT_ACTIVITY = {
    description: $t('New Activity'),
    detailed_description: '',
    owner_id: '',
    start_date: format(moment().toDate(), defaultDatePickerValueFormat),
    target_completion_date: format(moment().add(1, 'day').toDate(), defaultDatePickerValueFormat),
    estimated_completion_date: format(moment().add(1, 'day').toDate(), defaultDatePickerValueFormat),
    status: 0,
    completeness: 0,
    type: '',
    super_milestone: false,
    milestone: false,
};
const DEFAULT_ACTION = { id: null, description: '', owner_id: '', date: null, completed_date: null };

const ACTIVITY_TAB = 'edit-activity';
const DEPENDENCIES_TAB = 'dependencies';
const EMPTY_DEPENDENCIES = { predecessors: [], successors: [] };

const BaseProjectActivitySchema = Yup.object().shape({
    super_milestone: Yup.boolean().required(),
    milestone: Yup.boolean().required(),
    dependencies: Yup.array().of(
        Yup.object().shape({
            predecessor_id: Yup.number().required(),
            type: Yup.string().required($t('This value is required')),
            lag_days: lagSchema(),
        }),
    ),
});

const ProjectActivitySchema = BaseProjectActivitySchema.shape({
    description: Yup.string().required($t('This value is required')),
    detailed_description: Yup.string().nullable(),
    owner_id: Yup.string().nullable(),
    status: Yup.number().oneOf([0, 1, 2]),
    start_date: Yup.date().required($t('This value is required')),
    target_completion_date: Yup.date().min(Yup.ref('start_date'), "Target Completion Date can't be before Start Date").required($t('This value is required')),
    estimated_completion_date: Yup.date().nullable(),
    completeness: Yup.number().min(0).max(100),
    type: Yup.string().nullable(),
    attachments: Yup.mixed(),
    actions: Yup.array().of(
        Yup.object().shape({
            description: Yup.string().required($t('This value is required')),
            owner_id: Yup.string().nullable(),
            date: Yup.date().nullable(),
            completed_date: Yup.date().min(Yup.ref('date'), "Due Date can't be before Start Date").nullable(),
        }),
    ),
});

/** The links the save sends, in the shape the server expects. */
const dependencyPayload = (rows) => rows.map((row) => ({ predecessor_id: row.predecessor_id, type: row.type, lag_days: Number(row.lag_days) }));

const ProjectActivityForm = ({
    mode = 'edit',
    activityTabTitle = $t('Edit Activity'),
    extraTabs = [],
    dependencies = EMPTY_DEPENDENCIES,
    activity = {},
    actions = [],
    categoryId,
    updateUrl,
    storeUrl,
    deleteAttachmentUrl,
    downloadAttachmentUrl,
    onSuccess,
    onCreated,
    onCancel,
}) => {
    const [activeTab, setActiveTab] = useState(ACTIVITY_TAB);

    const { canManage } = useContext(ProjectPlanContext);

    // Only the predecessors the user may see are editable; the rest are kept as they are on save.
    const initialDependencies = useMemo(
        () =>
            dependencies.predecessors
                .filter((link) => link.activity?.visible)
                .map((link) => ({
                    predecessor_id: link.activity.id,
                    type: link.type,
                    lag_days: link.lag_days,
                    activity: link.activity,
                })),
        [dependencies],
    );
    const maskedPredecessors = useMemo(() => dependencies.predecessors.filter((link) => !link.activity?.visible), [dependencies]);

    return (
        <Formik
            enableReinitialize
            initialValues={{
                ...(!activity.id
                    ? { ...DEFAULT_ACTIVITY, category_id: categoryId }
                    : {
                          category_id: activity.category_id,
                          description: activity.description || '',
                          detailed_description: activity.detailed_description || '',
                          owner_id: activity.owner_id || '',
                          start_date: activity.start_date || null,
                          target_completion_date: activity.target_completion_date || null,
                          estimated_completion_date: activity.estimated_completion_date || null,
                          status: activity.status || 0,
                          completeness: activity.completeness || 0,
                          type: activity.type || '',
                          super_milestone: activity.super_milestone || false,
                          milestone: activity.milestone || false,
                      }),
                attachments: activity.attachments || [],
                actions: actions.map((action) => ({
                    id: action.id,
                    description: action.description,
                    owner_id: action.owner_id,
                    date: action.date,
                    completed_date: action.completed_date,
                })),
                dependencies: initialDependencies,
            }}
            validationSchema={activity.is_procurement_activity ? BaseProjectActivitySchema : ProjectActivitySchema}
            onSubmit={(values, actions) => {
                if (!canManage) {
                    return;
                }

                actions.setStatus(undefined);

                const { actions: activityActions, attachments: activityAttachments, dependencies: activityDependencies, ...activityData } = values;

                const formData = new FormData();
                Object.entries(activityData).forEach(([key, value]) => {
                    value !== null && formData.append(key, value);
                });
                formData.append('actions', JSON.stringify(activityActions));
                activityAttachments.forEach((attachment) => {
                    formData.append('attachments[]', attachment);
                });

                // Left out when unchanged, so the save leaves the links alone.
                const dependencyLinks = dependencyPayload(activityDependencies);
                if (!isEqual(dependencyLinks, dependencyPayload(initialDependencies))) {
                    formData.append('dependencies', JSON.stringify(dependencyLinks));
                    // The server removes only links the client had loaded, so concurrent additions survive.
                    formData.append('loaded_predecessor_ids', JSON.stringify(initialDependencies.map((link) => link.predecessor_id)));
                }

                const request = activity.id
                    ? axios.post(updateUrl.replace('ACTIVITY_ID', activity.id), formData, {
                          params: { _method: 'PUT' }, // This is a workaround to be able to send multipart/form-data as put request
                          headers: { 'Content-Type': 'multipart/form-data' },
                      })
                    : axios.post(storeUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

                request
                    .then((res) => {
                        actions.resetForm();
                        onCreated();
                    })
                    .catch((e) => {
                        const data = e?.response?.data || {};
                        const keys = Object.keys(data.errors || {});
                        keys.forEach((key) => {
                            actions.setFieldError(key, data.errors[key][0]);
                        });

                        if (data.error_code === 'DEPENDENCY_LOOP') {
                            actions.setStatus({ dependencyLoop: data.loop || [] });
                        }
                    })
                    .finally(() => {
                        actions.setSubmitting(false);
                    });
            }}
        >
            <ProjectActivityFormTabs
                activeTab={activeTab}
                onTabActivate={setActiveTab}
                activityTabTitle={activityTabTitle}
                extraTabs={extraTabs}
                maskedPredecessors={maskedPredecessors}
                successors={dependencies.successors}
                renderActivityTab={() => (
                    <ActivityDetailsTab
                        activity={activity}
                        maskedPredecessors={maskedPredecessors}
                        deleteAttachmentUrl={deleteAttachmentUrl}
                        downloadAttachmentUrl={downloadAttachmentUrl}
                        onSuccess={onSuccess}
                        onCancel={onCancel}
                    />
                )}
                renderDependenciesTab={() => (
                    <ActivityDependenciesTab
                        mode={mode}
                        activityId={activity.id}
                        categoryId={categoryId}
                        maskedPredecessors={maskedPredecessors}
                        successors={dependencies.successors}
                        onCancel={onCancel}
                    />
                )}
            />
        </Formik>
    );
};

/**
 * The tabs of the activity pop-up. The dependencies count follows the rows on screen, including
 * unsaved ones. A failed save opens the tab holding the first error.
 */
const ProjectActivityFormTabs = ({
    activeTab,
    onTabActivate,
    activityTabTitle,
    extraTabs,
    maskedPredecessors,
    successors,
    renderActivityTab,
    renderDependenciesTab,
}) => {
    const { values, errors, isSubmitting } = useFormikContext();

    const wasSubmittingRef = useRef(false);

    useEffect(() => {
        if (wasSubmittingRef.current && !isSubmitting) {
            const keys = Object.keys(errors || {});

            if (keys.includes('dependencies')) {
                onTabActivate(DEPENDENCIES_TAB);
            } else if (keys.length) {
                onTabActivate(ACTIVITY_TAB);
            }
        }

        wasSubmittingRef.current = isSubmitting;
    }, [isSubmitting]);

    const count = (values.dependencies?.length || 0) + maskedPredecessors.length + successors.length;

    // The render functions are rebuilt each render, so they are read through refs to keep the tab list stable.
    const renderActivityTabRef = useRef(renderActivityTab);
    renderActivityTabRef.current = renderActivityTab;
    const renderDependenciesTabRef = useRef(renderDependenciesTab);
    renderDependenciesTabRef.current = renderDependenciesTab;

    // Every tab is at least as tall as the Activity tab has been, so the pop-up and its buttons
    // stay put when switching tabs. A hidden panel measures 0, which keeps the last height.
    const [minHeight, setMinHeight] = useState(0);
    const observerRef = useRef(null);
    const measureActivityTab = useCallback((node) => {
        observerRef.current?.disconnect();
        observerRef.current = null;

        if (node) {
            // The fractional height, as a rounded-up offsetHeight would resize the observed node and loop.
            observerRef.current = new ResizeObserver(() => setMinHeight((height) => Math.max(height, node.getBoundingClientRect().height)));
            observerRef.current.observe(node);
        }
    }, []);

    const tabs = useMemo(
        () => [
            {
                id: ACTIVITY_TAB,
                title: activityTabTitle,
                content: () => (
                    <TabBody bodyRef={measureActivityTab} minHeight={minHeight}>
                        {renderActivityTabRef.current()}
                    </TabBody>
                ),
            },
            ...extraTabs.map((tab) => ({ ...tab, content: () => <TabBody minHeight={minHeight}>{tab.content()}</TabBody> })),
            {
                id: DEPENDENCIES_TAB,
                title: $t('Dependencies ({{count}})', { count }),
                content: () => <TabBody minHeight={minHeight}>{renderDependenciesTabRef.current()}</TabBody>,
            },
        ],
        [activityTabTitle, extraTabs, count, minHeight, measureActivityTab],
    );

    return <Tabs tabs={tabs} activeTab={activeTab} onTabActivate={onTabActivate} renderStrategy={RenderStrategyOnFirstVisit} />;
};

/** Each tab's root fills the body as a flex column, so its footer can sit at the bottom. */
const TabBody = ({ bodyRef, minHeight, children }) => (
    <div ref={bodyRef} className="tw-flex tw-flex-col *:tw-flex *:tw-grow *:tw-flex-col" style={{ minHeight }}>
        {children}
    </div>
);

const ActivityDetailsTab = ({ activity, maskedPredecessors, deleteAttachmentUrl, downloadAttachmentUrl, onSuccess, onCancel }) => {
    const attachmentInputRef = useRef(null);

    const { users } = useUsers();

    const projectPeriod = useContext(ProjectPeriodContext);
    const { activityTypes, statuses, completenessStatuses, canManage } = useContext(ProjectPlanContext);
    const { values, errors, touched, isSubmitting, setFieldValue } = useFormikContext();

    const earliestStart = useEarliestStart(maskedPredecessors);

    // The pickers bound every date to the project period, so Move start is offered only when the shifted dates stay inside it.
    const moveStartFitsPeriod = (days) => {
        if (!projectPeriod?.min && !projectPeriod?.max) {
            return true;
        }

        const shifted = Object.values(shiftDates(values, days)).filter(Boolean);
        const min = projectPeriod.min ? String(projectPeriod.min).substring(0, 10) : null;
        const max = projectPeriod.max ? String(projectPeriod.max).substring(0, 10) : null;

        return shifted.every((date) => (!min || date >= min) && (!max || date <= max));
    };

    const moveStart = (days) => {
        const shifted = shiftDates(values, days);

        setFieldValue('start_date', shifted.start_date);
        setFieldValue('target_completion_date', shifted.target_completion_date);

        if (values.estimated_completion_date) {
            setFieldValue('estimated_completion_date', shifted.estimated_completion_date);
        }
    };

    const deleteAttachment = (attachment) => {
        if (!canManage) {
            return;
        }

        if (!confirm($t('Are you sure you want delete this attachment?'))) {
            return;
        }

        const url = deleteAttachmentUrl.replace('ACTIVITY_ID', activity.id).replace('ATTACHMENT_ID', attachment.id);

        axios
            .delete(url)
            .then(() => {
                onSuccess();
            })
            .catch();
    };

    const downloadAttachment = (e, attachment) => {
        e.preventDefault();
        const url = downloadAttachmentUrl.replace('ACTIVITY_ID', activity.id).replace('ATTACHMENT_ID', attachment.id);

        axios({
            url: url,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.file_name);
            document.body.appendChild(link);
            link.click();
        });
    };

    return (
        <div className="tw-space-y-6">
            <div className="tw-grid tw-grid-cols-2 tw-gap-5">
                <div>
                    <TextFormField
                        label={$t('Name')}
                        required={true}
                        type="text"
                        name="description"
                        disabled={!canManage || activity.is_procurement_activity || isSubmitting}
                    />
                </div>
                <div>
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Status')}</label>
                        <Field
                            as="select"
                            className="select select-input !tw-border-none"
                            name="status"
                            onChange={(e) => setFieldValue('status', parseInt(e.target.value))}
                            style={{
                                paddingLeft: '8px',
                                paddingBottom: '2px',
                                paddingTop: '2px',
                                paddingRight: '30px',
                                fontSize: '12px',
                            }}
                            disabled={!canManage || isSubmitting}
                        >
                            {statuses.map((item, index) => {
                                return (
                                    <option key={index} value={index}>
                                        {item}
                                    </option>
                                );
                            })}
                        </Field>
                        {errors?.status && touched?.status && <div className="tw-text-red-500 tw-text-sm">{errors?.status}</div>}
                    </div>
                </div>
                <div className="tw-col-span-2">
                    <TextareaFormField
                        label={$t('Detailed Description')}
                        name="detailed_description"
                        disabled={!canManage || activity.is_procurement_activity || isSubmitting}
                    />
                </div>
                <div className="tw-col-span-2">
                    <div className="tw-flex tw-flex-col tw-flex-shrink tw-gap-y-2.5">
                        <div className="tw-text-xs tw-text-charcoal">
                            {$t('Type')} <span>*</span>
                        </div>
                        <div className="tw-h-8.5 tw-flex tw-items-center tw-gap-x-10">
                            {Object.entries(activityTypes).map(([type, label]) => (
                                <label
                                    key={type}
                                    className="input-checkbox__label tw-flex tw-items-center tw-gap-x-2 tw-text-charcoal-50 tw-font-semibold tw-text-sm"
                                >
                                    <Field type="radio" name="type" value={type} />
                                    {label}
                                </label>
                            ))}
                            {values.type && (
                                <TextButton variant="primary" onClick={() => setFieldValue('type', '')}>
                                    <XMarkIcon className="tw-w-2.5 tw-h-2.5" />
                                    {$t('Clear Selection')}
                                </TextButton>
                            )}
                        </div>
                        {errors?.type && touched?.type && <div className="tw-text-red-500 tw-text-sm">{errors?.type}</div>}
                    </div>
                </div>
                <div>
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label" htmlFor="owner_id">
                            {$t('Owner')}
                        </label>
                        <UserSelectorInput
                            className="react-select-container"
                            classNamePrefix="react-select"
                            selectedId={values.owner_id}
                            onChange={(value) => setFieldValue('owner_id', value)}
                            usersList={users}
                            fetchIfNoUsers={false}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    height: 32,
                                    minHeight: 32,
                                    backgroundColor: 'rgb(228 228 228 / 0.5)',
                                }),
                            }}
                            disabled={isSubmitting}
                        />
                        {errors?.owner_id && touched?.owner_id && <div className="tw-text-red-500 tw-text-sm">{errors?.owner_id}</div>}
                    </div>
                </div>
                <div>
                    <TextFormField
                        label={$t('Progress')}
                        required={true}
                        type="number"
                        name="completeness"
                        min={0}
                        max={100}
                        disabled={parseInt(values.status) !== parseInt(completenessStatuses.in_progress) || !canManage || isSubmitting}
                    />
                </div>
                <div>
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">
                            {`${$t('Start Date & Target Completion Date')}`} <span>*</span>
                        </label>
                        <div>
                            <DateRangePickerField
                                className={
                                    (errors?.start_date && touched?.start_date) || (errors?.target_completion_date && touched?.target_completion_date)
                                        ? 'tw-border tw-border-red-500 text-input !tw-bg-grey/50'
                                        : 'tw-border-0 text-input !tw-bg-grey/50'
                                }
                                nameStart="start_date"
                                nameEnd="target_completion_date"
                                startDate={values.start_date}
                                endDate={values.target_completion_date}
                                min={projectPeriod.min}
                                max={projectPeriod.max}
                                valueFormat={defaultDatePickerValueFormat}
                                dateFormat={defaultDatePickerFormat}
                                onChange={([start, end]) => {
                                    setFieldValue('start_date', start);
                                    setFieldValue('target_completion_date', end);
                                }}
                            />
                        </div>
                        {errors?.start_date && touched?.start_date && <div className="tw-text-red-500 tw-text-sm">{errors?.start_date}</div>}
                        {errors?.target_completion_date && touched?.target_completion_date && (
                            <div className="tw-text-red-500 tw-text-sm">{errors?.target_completion_date}</div>
                        )}
                    </div>
                </div>
                <div>
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Estimated Completion Date')}</label>
                        <div>
                            <DatePickerField
                                name="estimated_completion_date"
                                className={
                                    errors?.estimated_completion_date && touched?.estimated_completion_date
                                        ? 'tw-border tw-border-red-500 text-input !tw-bg-grey/50'
                                        : 'tw-border-0 text-input !tw-bg-grey/50'
                                }
                                value={values.estimated_completion_date}
                                valueFormat={defaultDatePickerValueFormat}
                                dateFormat={defaultDatePickerFormat}
                                onChange={setFieldValue}
                                min={projectPeriod.min}
                                max={projectPeriod.max}
                                disabled={isSubmitting}
                            />
                        </div>
                        {errors?.estimated_completion_date && touched?.estimated_completion_date && (
                            <div className="tw-text-red-500 tw-text-sm">{errors?.estimated_completion_date}</div>
                        )}
                    </div>
                </div>
                <div className="tw-col-span-2">
                    <ActivityEarliestStartLine
                        earliestStartDate={earliestStart}
                        startDate={values.start_date}
                        isComplete={parseInt(values.status) === parseInt(completenessStatuses.complete)}
                        canMoveStart={!!canManage && !isSubmitting && !activity.is_procurement_activity}
                        canMoveStartBy={moveStartFitsPeriod}
                        onMoveStart={moveStart}
                    />
                </div>
            </div>
            <hr />
            <div className="tw-flex tw-flex-col tw-space-y-4">
                <FormSectionHeading heading={$t('Attachments')}></FormSectionHeading>
                <div className="tw-grid tw-grid-cols-1">
                    {!!canManage && (
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label htmlFor="attachments" className="text-input__label">
                                {$t('Select Attachments')}
                            </label>
                            <input
                                ref={attachmentInputRef}
                                type="file"
                                className={errors.attachments && touched.attachments ? 'tw-border tw-border-red-500 file-input' : 'tw-border-0 file-input'}
                                name="attachments"
                                multiple
                                accept={ACCEPTED_ATTACHMENT_FILE_TYPES}
                                onChange={(event) => {
                                    setFieldValue('attachments', Array.from(event.currentTarget.files));
                                }}
                            />
                        </div>
                    )}
                </div>
                {!!activity?.attachments?.length && (
                    <Attachments attachments={activity.attachments} onDelete={deleteAttachment} onDownload={downloadAttachment} canManage={canManage} />
                )}
            </div>
            <hr />
            <div>
                <FieldArray name="actions">
                    {(arrayHelpers) => (
                        <div className="tw-flex tw-flex-col tw-gap-y-2">
                            <div className="tw-flex tw-items-end tw-justify-between">
                                <div className="tw-font-medium">{$t('Actions')}</div>
                                <Button variant="primary" onClick={() => arrayHelpers.unshift(DEFAULT_ACTION)}>
                                    {$t('Add Action')}
                                </Button>
                            </div>
                            <div className="tw-inline-block tw-space-y-5 tw-max-h-64 tw-overflow-y-auto tw-pb-1">
                                {values.actions?.map((action, index) => {
                                    const actionErrors = (errors?.actions || [])[index];
                                    const actionTouched = (touched?.actions || [])[index];

                                    return (
                                        <div key={index} className="tw-flex tw-items-start tw-gap-x-5">
                                            <div className="tw-grow">
                                                <TextFormField
                                                    label={$t('Name')}
                                                    required={true}
                                                    type="text"
                                                    name={`actions.${index}.description`}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            <div className="tw-w-32">
                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label className="text-input__label" htmlFor="owner_id">
                                                        {$t('Owner')}
                                                    </label>
                                                    <UserSelectorInput
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        selectedId={action.owner_id || ''}
                                                        onChange={(value) => setFieldValue(`actions.${index}.owner_id`, value)}
                                                        usersList={users}
                                                        fetchIfNoUsers={false}
                                                        styles={{
                                                            control: (baseStyles, state) => ({
                                                                ...baseStyles,
                                                                height: 32,
                                                                minHeight: 32,
                                                                backgroundColor: 'rgb(228 228 228 / 0.5)',
                                                            }),
                                                        }}
                                                        disabled={isSubmitting}
                                                    />
                                                    {actionErrors?.owner_id && actionTouched?.owner_id && (
                                                        <div className="tw-text-red-500 tw-text-sm">{actionErrors?.owner_id}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="tw-w-28">
                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label className="text-input__label">{$t('Start Date')}</label>
                                                    <div>
                                                        <DatePickerField
                                                            name={`actions.${index}.date`}
                                                            className={
                                                                actionErrors?.date && actionTouched?.date
                                                                    ? 'tw-border tw-border-red-500 text-input !tw-bg-grey/50'
                                                                    : 'tw-border-0 text-input !tw-bg-grey/50'
                                                            }
                                                            value={values.actions[index]?.date}
                                                            valueFormat={defaultDatePickerValueFormat}
                                                            dateFormat={defaultDatePickerFormat}
                                                            onChange={setFieldValue}
                                                            min={projectPeriod.min}
                                                            max={projectPeriod.max}
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    {actionErrors?.date && actionTouched?.date && (
                                                        <div className="tw-text-red-500 tw-text-sm">{actionErrors?.date}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="tw-w-28">
                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label className="text-input__label">{$t('Due Date')}</label>
                                                    <div>
                                                        <DatePickerField
                                                            name={`actions.${index}.completed_date`}
                                                            className={
                                                                actionErrors?.completed_date && actionTouched?.completed_date
                                                                    ? 'tw-border tw-border-red-500 text-input !tw-bg-grey/50'
                                                                    : 'tw-border-0 text-input !tw-bg-grey/50'
                                                            }
                                                            value={values.actions[index]?.completed_date}
                                                            valueFormat={defaultDatePickerValueFormat}
                                                            dateFormat={defaultDatePickerFormat}
                                                            onChange={setFieldValue}
                                                            min={projectPeriod.min}
                                                            max={projectPeriod.max}
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    {actionErrors?.completed_date && actionTouched?.completed_date && (
                                                        <div className="tw-text-red-500 tw-text-sm">{actionErrors?.completed_date}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="tw-min-h-[58px] tw-flex tw-items-end">
                                                <Button variant="danger" onClick={() => arrayHelpers.remove(index)} disabled={isSubmitting}>
                                                    {$t('Remove')}
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </FieldArray>
            </div>

            <ProjectActivityFormFooter onCancel={onCancel} />
        </div>
    );
};

function Attachments({ attachments, onDelete, onDownload }) {
    const { canManage } = useContext(ProjectPlanContext);

    return (
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            {attachments.map((attachment) => (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2" key={attachment.id}>
                    <a href="#" className="long-text-wrap" onClick={(e) => onDownload(e, attachment)}>
                        {attachment.file_name}
                    </a>
                    {!!canManage && (
                        <Button variant="primary" onClick={() => onDelete(attachment)}>
                            <TrashIcon className="tw-w-3 tw-h-3" />
                            {$t('Delete')}
                        </Button>
                    )}
                </div>
            ))}
        </div>
    );
}

export default ProjectActivityForm;
